/**
 * Offline / failed-save fallback for community contributions.
 *
 * When `submitTag` or `submitFeedback` can't reach the backend (offline,
 * fetch error, transient 5xx) — or fails an auth-gated insert before the
 * user has signed in — we stash the payload in localStorage and retry it
 * later. Auto-flush triggers:
 *   • app boot (one immediate attempt)
 *   • `window` `online` event
 *   • Supabase auth state change (user just signed in)
 *
 * Callers don't need to know any of this: the API functions enqueue then
 * resolve successfully so the UI shows its normal "saved" state, and the
 * queue surfaces a single low-key toast explaining that the contribution
 * will sync automatically.
 */
import { supabase } from "@/integrations/supabase/client";
import { contributeToSegment } from "@/api/roadSegments";
import { toast } from "sonner";
import type { TravelMode } from "@/api/routing";

const STORAGE_KEY = "pleasant.pendingContributions.v1";
const MAX_QUEUE = 200;

export type PendingTagPayload = {
  /** Client-assigned primary key. Makes retry idempotent: a duplicate insert
   *  fails on the PK (23505), which the flusher reads as "already landed"
   *  rather than trying forever or double-posting. */
  id?: string;
  user_id: string;
  lat: number;
  lng: number;
  travel_mode: string;
  tag_label: string;
  sentiment: string;
  pleasantness: number | null;
  source: string;
  report_kind: string | null;
};

export type PendingFeedbackPayload = {
  user_id: string;
  travel_mode: string;
  rating: number;
  tags: string[];
  start_lat: number | null;
  start_lng: number | null;
  end_lat: number | null;
  end_lng: number | null;
};

/** Everything needed to resolve a road AFTER the fact, for a contribution that
 *  was made with no connectivity. Held on the device only. */
export interface RoadResolveContext {
  /** The raw point the contribution was made at. NEVER uploaded as-is. */
  point: [number, number];
  mode: "driving" | "walking" | "biking";
  /** Reported accuracy of that fix, metres. null = none reported. */
  accuracyM: number | null;
  /** True when the user tapped the spot rather than it coming from GPS. */
  userPlacedPoint?: boolean;
}

export type PendingItem =
  | {
      id: string;
      kind: "tag";
      enqueuedAt: number;
      attempts: number;
      payload: PendingTagPayload;
      /** Extra info used to re-trigger the proprietary segment layer
       *  contribution after the tag itself flushes successfully. */
      segmentKeyOverride?: string;
      mode: TravelMode;
      /** R1. TRUE means payload.lat/lng is still the contributor's RAW point
       *  and this item MUST NOT be inserted. Reconnecting resolves it to a road
       *  first; if that can't be done confidently the item stays pending. */
      needsRoadResolution?: boolean;
      /** How to resolve it later. Present iff needsRoadResolution. */
      resolveContext?: RoadResolveContext;
      /** Set when resolution came back ambiguous — the user has to pick. The
       *  UI surfaces these in My Contributions; the flusher leaves them alone. */
      awaitingUser?: boolean;
    }
  | {
      id: string;
      kind: "feedback";
      enqueuedAt: number;
      attempts: number;
      payload: PendingFeedbackPayload;
    };

// ---------------------------------------------------------------------------
// Storage helpers
// ---------------------------------------------------------------------------

function safeRead(): PendingItem[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as PendingItem[]) : [];
  } catch {
    return [];
  }
}

function safeWrite(items: PendingItem[]): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items.slice(-MAX_QUEUE)));
  } catch {
    /* quota / private mode — best effort */
  }
}

function newId(): string {
  // Crypto when available, otherwise fall back so SSR/jsdom paths don't crash.
  try {
    if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
      return crypto.randomUUID();
    }
  } catch {
    /* ignore */
  }
  return `p_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

// ---------------------------------------------------------------------------
// Public queue API
// ---------------------------------------------------------------------------

export function getPending(): PendingItem[] {
  return safeRead();
}

export function getPendingCount(): number {
  return safeRead().length;
}

export function clearPending(): void {
  safeWrite([]);
}

export function enqueueTag(args: {
  payload: PendingTagPayload;
  segmentKeyOverride?: string;
  mode: TravelMode;
  showToast?: boolean;
  /** R1: the contribution has NOT been resolved to a road yet. */
  needsRoadResolution?: boolean;
  resolveContext?: RoadResolveContext;
}): void {
  const items = safeRead();
  items.push({
    id: newId(),
    kind: "tag",
    enqueuedAt: Date.now(),
    attempts: 0,
    // Stamp the row's primary key now so a retry can never double-insert.
    payload: { ...args.payload, id: args.payload.id ?? newId() },
    segmentKeyOverride: args.segmentKeyOverride,
    mode: args.mode,
    needsRoadResolution: args.needsRoadResolution,
    resolveContext: args.resolveContext,
  });
  safeWrite(items);
  if (args.showToast !== false) {
    if (args.needsRoadResolution) notifyAwaitingRoad();
    else notifyEnqueued();
  }
}

/** How many queued contributions are waiting for the user to pick a road. */
export function getAwaitingUserCount(): number {
  return safeRead().filter((i) => i.kind === "tag" && i.awaitingUser).length;
}

/** The queued contributions that need the user to choose a road. */
export function getAwaitingUser(): PendingItem[] {
  return safeRead().filter((i) => i.kind === "tag" && i.awaitingUser);
}

/** Resolve one queued item against a road the user just picked. */
export function resolveQueuedTag(itemId: string, road: {
  representative: [number, number]; segmentRef: string; distM: number;
}): void {
  const items = safeRead();
  const next = items.map((i) => {
    if (i.id !== itemId || i.kind !== "tag") return i;
    return {
      ...i,
      payload: {
        ...i.payload,
        lat: road.representative[1],
        lng: road.representative[0],
        source: "user_confirmed_road",
        snap_dist_m: Math.round(road.distM),
      } as PendingTagPayload,
      segmentKeyOverride: road.segmentRef,
      needsRoadResolution: false,
      awaitingUser: false,
    };
  });
  safeWrite(next);
}

export function enqueueFeedback(args: {
  payload: PendingFeedbackPayload;
  showToast?: boolean;
}): void {
  const items = safeRead();
  items.push({
    id: newId(),
    kind: "feedback",
    enqueuedAt: Date.now(),
    attempts: 0,
    payload: args.payload,
  });
  safeWrite(items);
  if (args.showToast !== false) notifyEnqueued();
}

function notifyAwaitingRoad(): void {
  if (typeof window === "undefined") return;
  const now = Date.now();
  if (now - lastToastAt < 4000) return;
  lastToastAt = now;
  toast("Saved — we'll match it to the road", {
    description: "No signal to identify the road yet. It'll finish when you reconnect.",
  });
}

let lastToastAt = 0;
function notifyEnqueued(): void {
  if (typeof window === "undefined") return;
  // Throttle to avoid stacking toasts when several writes fail in a row.
  const now = Date.now();
  if (now - lastToastAt < 4000) return;
  lastToastAt = now;
  toast("Saved offline — will sync automatically", {
    description: navigator?.onLine === false
      ? "You're offline. Your contribution will upload when you reconnect."
      : "We'll retry as soon as the connection is restored.",
  });
}

// ---------------------------------------------------------------------------
// Flush
// ---------------------------------------------------------------------------

let flushing = false;

export interface FlushResult {
  flushed: number;
  remaining: number;
  failed: number;
}

export async function flushPending(): Promise<FlushResult> {
  if (typeof window === "undefined") {
    return { flushed: 0, remaining: 0, failed: 0 };
  }
  if (flushing) return { flushed: 0, remaining: getPendingCount(), failed: 0 };
  flushing = true;
  try {
    if (typeof navigator !== "undefined" && navigator.onLine === false) {
      return { flushed: 0, remaining: getPendingCount(), failed: 0 };
    }
    const items = safeRead();
    if (items.length === 0) return { flushed: 0, remaining: 0, failed: 0 };

    const remaining: PendingItem[] = [];
    let flushed = 0;
    let failed = 0;

    for (const item of items) {
      const next = { ...item, attempts: item.attempts + 1 };
      try {
        if (item.kind === "tag") {
          // ---------------------------------------------------------------
          // R1 GATE. Reaching the network is NOT permission to upload.
          //
          // An item flagged needsRoadResolution still holds the contributor's
          // RAW point in payload.lat/lng. It may only be inserted once that
          // point has been resolved to a road and the payload rewritten to the
          // road's representative point. If resolution can't be done, the item
          // stays pending — it is never uploaded as-is, and never published.
          // ---------------------------------------------------------------
          if (item.awaitingUser) {
            // Parked for the user to pick a road. Not a failure, so no attempt
            // is spent and nothing is retried until they answer.
            remaining.push(item);
            continue;
          }

          let toInsert = item.payload;
          let segKey = item.segmentKeyOverride;

          if (item.needsRoadResolution) {
            const ctx = item.resolveContext;
            if (!ctx) {
              // Flagged unresolved with no way to resolve. Refuse to publish.
              console.warn("[pendingContributions] unresolved tag with no context — held", item.id);
              remaining.push(item);
              continue;
            }
            const { resolveRoadAt } = await import("@/utils/roadResolve");
            const res = await resolveRoadAt(ctx.point, ctx.mode, {
              accuracyM: ctx.accuracyM,
              userPlacedPoint: ctx.userPlacedPoint,
            });
            if (res.status === "confident") {
              toInsert = {
                ...item.payload,
                lat: res.chosen.representative[1],
                lng: res.chosen.representative[0],
                snap_dist_m: Math.round(res.chosen.distM),
              } as typeof item.payload;
              segKey = res.chosen.segmentRef;
            } else if (res.status === "ambiguous") {
              // Several roads fit. Ask the user rather than guessing on their
              // behalf hours after the fact.
              remaining.push({ ...item, awaitingUser: true });
              continue;
            } else {
              // Still no road data here. Keep it; try again next flush.
              remaining.push(next);
              continue;
            }
          }

          const { error } = await supabase
            .from("community_tags")
            .insert(toInsert);
          // 23505 = this row's client-assigned primary key already landed, so a
          // previous attempt succeeded and we lost the acknowledgement. That is
          // success, not a retry — this is what makes the flush idempotent.
          if (error && (error as { code?: string }).code !== "23505") throw error;
          flushed += 1;
          // Best-effort segment-layer feed; never re-queue this part. Uses the
          // RESOLVED coordinates, so an unresolved point can never reach the
          // aggregated road-segment layer either.
          void contributeToSegment({
            lat: toInsert.lat,
            lng: toInsert.lng,
            mode: item.mode,
            sentiment: toInsert.sentiment as "positive" | "negative",
            tagLabel: toInsert.tag_label,
            segmentKeyOverride: segKey,
          }).catch(() => {});
        } else {
          const { error } = await supabase
            .from("route_feedback")
            .insert(item.payload);
          if (error) throw error;
          flushed += 1;
        }
      } catch (err) {
        failed += 1;
        // Drop after too many tries so a permanently invalid row can't wedge
        // the queue forever.
        // An unresolved contribution is not "permanently invalid" — it is
        // waiting for road data. Only genuinely failing writes age out.
        if (next.attempts < 8 || (next.kind === "tag" && next.needsRoadResolution)) {
          remaining.push(next);
        } else {
           
          console.warn("[pendingContributions] dropping after 8 attempts", {
            id: item.id,
            kind: item.kind,
            err,
          });
        }
      }
    }

    safeWrite(remaining);

    if (flushed > 0) {
      toast.success(
        flushed === 1
          ? "Synced 1 offline contribution"
          : `Synced ${flushed} offline contributions`,
      );
    }

    return { flushed, remaining: remaining.length, failed };
  } finally {
    flushing = false;
  }
}

// ---------------------------------------------------------------------------
// Auto-flush wiring (idempotent)
// ---------------------------------------------------------------------------

let installed = false;

export function attachAutoFlush(): void {
  if (installed || typeof window === "undefined") return;
  installed = true;

  const trigger = () => {
    void flushPending();
  };

  window.addEventListener("online", trigger);
  window.addEventListener("focus", trigger);

  try {
    supabase.auth.onAuthStateChange((event) => {
      if (event === "SIGNED_IN" || event === "TOKEN_REFRESHED") trigger();
    });
  } catch {
    /* ignore */
  }

  // One immediate attempt on boot (covers items left over from a prior session).
  trigger();
}
